import { AxiosError } from 'axios';
import { User } from './user';
import api from './axios';
import { UserRole } from './user';

export interface LoginResponse {
  user: User;
  access_token: string;
  refresh_token: string;
  expires_in?: number;
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  user: User;
  expires_in?: number;
}

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export type RegistrationRequestStatus = 'pending' | 'approved' | 'rejected';

export interface RegisterRequestPayload {
  name: string;
  email: string;
  phone: string;
  farmName: string;
  message?: string;
}

export interface RegistrationRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  farmName: string;
  message?: string;
  status: RegistrationRequestStatus;
  reviewedBy?: string;
  reviewNotes?: string;
  approvedTenantId?: string;
  approvedUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApproveRegistrationRequestPayload {
  tenantId: string;
  role?: UserRole;
  password?: string;
  reviewNotes?: string;
}

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    console.log('Sending login request...');
    console.log('API URL:', api.defaults.baseURL);
    console.log('Request URL:', `${api.defaults.baseURL}/auth/login`);

    const response = await api.post<BackendResponse<LoginResponse>>('/auth/login', { email, password });
    console.log('Login response:', {
      status: response.status,
      headers: response.headers,
      data: response.data,
      fullResponse: response  // Log the full response object
    });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;

    // Check if the response has the wrapped format
    if (responseData && responseData.data && responseData.data.access_token) {
      return responseData.data;
    }

    console.error('Invalid login response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error: unknown) {
    const errorDetails: any = {
      message: error instanceof Error ? error.message : 'Unknown error',
    };

    if (error && typeof error === 'object' && 'isAxiosError' in error) {
      const axiosError = error as any;
      errorDetails.isAxiosError = true;
      if (axiosError.response) {
        errorDetails.response = {
          status: axiosError.response.status,
          data: axiosError.response.data,
          headers: axiosError.response.headers
        };
      } else {
        errorDetails.response = 'No response';
      }
      console.error('Axios error details:', {
        config: axiosError.config,
        request: axiosError.request,
        response: axiosError.response
      });
    } else {
      errorDetails.isAxiosError = false;
    }

    console.error('Login error details:', errorDetails);
    throw error instanceof Error ? error : new Error('Login failed');
  }
};

export const googleLogin = async (token: string): Promise<LoginResponse> => {
  try {
    const response = await api.post<BackendResponse<LoginResponse>>('/auth/google', { token });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    if (responseData && responseData.data && responseData.data.access_token) {
      return responseData.data;
    }

    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Google login error:', error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

export const getCurrentUser = async (): Promise<User> => {
  console.log('API: Fetching current user...');
  try {
    const response = await api.get<BackendResponse<User>>('/users/me');
    console.log('API: Current user response:', {
      status: response.status,
      data: response.data,
      headers: response.headers
    });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }

    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('API: Failed to fetch current user:', {
      error,
      message: error instanceof Error ? error.message : 'Unknown error',
      isAxiosError: (error as any)?.isAxiosError,
      response: (error as any)?.response ? {
        status: (error as any).response.status,
        data: (error as any).response.data,
        headers: (error as any).response.headers
      } : 'No response'
    });
    throw error;
  }
};

export const refreshToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  try {
    const response = await api.post<BackendResponse<RefreshTokenResponse>>('/auth/refresh-token', { refresh_token: refreshToken });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }

    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

export const registerRequest = async (payload: RegisterRequestPayload): Promise<{ message: string; success: boolean }> => {
  try {
    const response = await api.post<BackendResponse<{ message: string; success: boolean }>>('/auth/register-request', payload);
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error submitting register request:', error);
    throw error;
  }
};

export const fetchRegistrationRequests = async (status?: RegistrationRequestStatus): Promise<RegistrationRequest[]> => {
  try {
    const response = await api.get<BackendResponse<RegistrationRequest[]>>('/auth/register-requests', {
      params: status ? { status } : undefined,
    });
    const responseData = response.data;
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching registration requests:', error);
    throw error;
  }
};

export const approveRegistrationRequest = async (
  requestId: string,
  payload: ApproveRegistrationRequestPayload,
): Promise<{ message: string; success: boolean }> => {
  if (!requestId || !requestId.trim()) {
    throw new Error('Invalid registration request id');
  }

  try {
    const response = await api.patch<BackendResponse<{ message: string; success: boolean }>>(
      `/auth/register-requests/${requestId.trim()}/approve`,
      payload,
    );
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error approving registration request:', error);
    throw error;
  }
};

export const rejectRegistrationRequest = async (
  requestId: string,
  reviewNotes?: string,
): Promise<{ message: string; success: boolean }> => {
  if (!requestId || !requestId.trim()) {
    throw new Error('Invalid registration request id');
  }

  try {
    const response = await api.patch<BackendResponse<{ message: string; success: boolean }>>(
      `/auth/register-requests/${requestId.trim()}/reject`,
      { reviewNotes },
    );
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error rejecting registration request:', error);
    throw error;
  }
};

export const forgotPassword = async (email: string): Promise<{ message: string; success: boolean }> => {
  try {
    const response = await api.post<BackendResponse<{ message: string; success: boolean }>>('/auth/forgot-password', { email });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }

    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
};

export const verifyResetToken = async (token: string): Promise<{ message: string; success: boolean; email: string }> => {
  try {
    const response = await api.post<BackendResponse<{ message: string; success: boolean; email: string }>>('/auth/verify-reset-token', { token });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }

    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error verifying reset token:', error);
    throw error;
  }
};

export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string; success: boolean }> => {
  try {
    const response = await api.post<BackendResponse<{ message: string; success: boolean }>>('/auth/reset-password', { token, newPassword });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    if (responseData && responseData.data) {
      return responseData.data;
    }

    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};
